import React, { useEffect, useState } from "react";
import { BsBookmark, BsFileEarmarkText, BsPeople } from "react-icons/bs";
import {
  FaFilePdf,
  FaFileExcel,
  FaFileWord,
  FaFileImage,
  FaFileAlt,
  FaPhoneAlt,
} from "react-icons/fa";
import { FaUnlockKeyhole } from "react-icons/fa6";
import { MdOutlineFileDownload, MdVisibility } from "react-icons/md";
import { Spin, Tooltip } from "antd";
import { GetAdminDashboardData } from "../../../Api/SuperAdminVault/Home";
import {
  RequestOtpForViewSensitiveDocument,
  ValidateDocumentOtp,
} from "../../../Api/SuperAdminVault/Documents";
import DocumentOtpModal from "../Documents/DocumentOtpModal";
import SecretAccessModal from "../Credentials/SecretAccessModal";
import ContactViewModal from "../Contacts/ContactViewModal";

const Home = ({ setActiveTab }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpDocument, setOtpDocument] = useState(null);

  const [secretModalOpen, setSecretModalOpen] = useState(false);
  const [selectedCredId, setSelectedCredId] = useState(null);

  const [contactViewOpen, setContactViewOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const [verified, setVerified] = useState(false);
  const [otpPurpose, setOtpPurpose] = useState("view_credentials");

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const data = await GetAdminDashboardData();
      setDashboardData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const getUserId = () => {
    return "001";
  };

  const downloadBlob = (blob, fileName = "document") => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName || "document";
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handlePinnedDocumentDownload = async (item) => {
    if (!item?.document_id) return;

    const response = await RequestOtpForViewSensitiveDocument({
      user_id: getUserId(),
      document_id: item.document_id,
    });

    if (response?.action === "OTP_REQUIRED") {
      setOtpDocument(item);
      setOtpModalOpen(true);
      return;
    }

    if (response?.blob) {
      downloadBlob(response.blob, item?.original_file_name);
    }
  };

  const handleHomeDocumentOtpVerify = async (encryptedOtp) => {
    if (!otpDocument?.document_id) return;

    const response = await ValidateDocumentOtp({
      user_id: getUserId(),
      document_id: otpDocument.document_id,
      otp: encryptedOtp,
    });

    if (response?.blob) {
      downloadBlob(
        response.blob,
        otpDocument?.original_file_name ||
          otpDocument?.document_name ||
          "document",
      );

      setOtpModalOpen(false);
      setOtpDocument(null);
    }
  };

  const handlePinnedView = (item) => {
    if (item?.cred_id) {
      setOtpPurpose("view_credentials");
      setSelectedCredId(item.cred_id);
      setSecretModalOpen(true);
      return;
    }

    if (item?.contact_id) {
      setSelectedContact(item);
      setContactViewOpen(true);
    }
  };

  const vaultCards = [
    {
      title: "Credentials",
      count: dashboardData?.credentials || 0,
      description: "Logins, API keys, tokens",
      icon: <BsBookmark />,
      tab: "credentials",
    },
    {
      title: "Documents",
      count: dashboardData?.documents || 0,
      description: "PDFs, contracts, exports",
      icon: <BsFileEarmarkText />,
      tab: "documents",
    },
    {
      title: "Contacts",
      count: dashboardData?.contact || 0,
      description: "Partners & escalation POCs",
      icon: <BsPeople />,
      tab: "contacts",
    },
  ];

  const pinnedCredentials = dashboardData?.pinned_credentials || [];
  const recentActivity = dashboardData?.recent_activity || [];

  const formatAction = (action) => {
    if (!action) return "-";
    return action.replaceAll("_", " ");
  };

  const getPinnedTitle = (item) => {
    return (
      item.credential_name ||
      item.original_file_name ||
      item.document_name ||
      item.contact_name ||
      "-"
    );
  };
  const fileTypeColor = (type) => {
    const t = String(type || "").toLowerCase();

    if (t.includes("pdf")) return { label: "PDF" };

    if (
      t.includes("spreadsheet") ||
      t.includes("excel") ||
      t.includes("xlsx") ||
      t.includes("xls")
    ) {
      return { label: "XLS" };
    }

    if (
      t.includes("wordprocessingml") ||
      t.includes("msword") ||
      t.includes("document") ||
      t.includes("docx") ||
      t.includes("doc")
    ) {
      return { label: "DOC" };
    }

    if (
      t.includes("image") ||
      t.includes("png") ||
      t.includes("jpg") ||
      t.includes("jpeg")
    ) {
      return { label: "IMG" };
    }

    return { label: "FILE" };
  };
  const getPinnedSubtitle = (item) => {
    if (item.cred_id) {
      return item.credential_type || "-";
    }

    if (item.document_id) {
      const { label } = fileTypeColor(item.file_type || item.mime_type);
      return label;
    }

    if (item.contact_id) {
      return item.category || "-";
    }

    return "-";
  };

  const getPinnedType = (item) => {
    if (item.cred_id) {
      return <FaUnlockKeyhole size="0.95vw" />;
    }

    if (item.document_id) {
      const type = String(item.file_type || item.mime_type || "").toLowerCase();

      if (type.includes("pdf")) return <FaFilePdf size="0.95vw" />;
      if (
        type.includes("excel") ||
        type.includes("spreadsheet") ||
        type.includes("xls") ||
        type.includes("xlsx")
      ) {
        return <FaFileExcel size="0.95vw" />;
      }
      if (
        type.includes("word") ||
        type.includes("document") ||
        type.includes("doc")
      ) {
        return <FaFileWord size="0.95vw" />;
      }
      if (
        type.includes("image") ||
        type.includes("jpeg") ||
        type.includes("jpg") ||
        type.includes("png")
      ) {
        return <FaFileImage size="0.95vw" />;
      }

      return <FaFileAlt size="0.95vw" />;
    }

    if (item.contact_id) {
      return <FaPhoneAlt size="0.95vw" />;
    }

    return "-";
  };

  return (
    <div className="w-full h-full flex flex-col gap-[1vw] relative">
      {loading && (
        <div className="absolute inset-0 bg-white/50 z-20 flex items-center justify-center">
          <Spin size="large" />
        </div>
      )}

      <div className="flex gap-[1vw] h-[36%] min-h-[15vw]">
        <div className="w-[65%] bg-white/75 rounded-[0.9vw] border-[0.1vw] border-[#1F4B7F] overflow-hidden shadow-sm">
          <div className="bg-[#1F4B7F] text-white px-[1vw] h-[6vh] flex justify-between items-center">
            <h3 className="text-[1.05vw] font-bold">Modules Summary</h3>
          </div>

          <div className="grid grid-cols-3 gap-[1vw] p-[1.1vw]">
            {vaultCards.map((item) => (
              <button
                key={item.title}
                onClick={() => setActiveTab(item.tab)}
                className="relative bg-gradient-to-br from-white to-[#F5F8FC] border border-[#D7DFEA] rounded-[0.9vw] p-[1vw] min-h-[8vw] hover:shadow-lg hover:border-[#1F4B7F] transition-all duration-300 text-left"
              >
                <div className="flex gap-[0.8vw] absolute top-[1vw] left-[1vw] right-[1vw]">
                  <div className="w-[3vw] h-[3vw] rounded-[0.8vw] bg-[#1F4B7F] text-white flex items-center justify-center text-[1.3vw] shrink-0">
                    {item.icon}
                  </div>

                  <div>
                    <h3 className="text-[#1F4B7F] text-[1vw] font-bold">
                      {item.title}
                    </h3>

                    <p className="text-[#7C879D] text-[0.72vw] mt-[0.15vw]">
                      {item.description}
                    </p>
                  </div>
                </div>

                <div className="absolute bottom-[0.7vw] right-[1vw]">
                  <span className="text-[#1F4B7F] text-[3vw] font-bold leading-none">
                    {item.count}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="w-[35%] bg-white/75 rounded-[0.9vw] border-[0.1vw] border-[#1F4B7F] overflow-hidden shadow-sm flex flex-col">
          <div className="bg-[#1F4B7F] text-white px-[1vw] h-[6vh] flex items-center shrink-0">
            <h3 className="text-[1.05vw] font-bold">Vault Summary</h3>
          </div>

          <div className="flex-1 grid grid-cols-2 gap-[0.8vw] p-[1vw]">
            <div className="bg-[#E9EEF5] rounded-[0.65vw] p-[0.5vw] flex flex-col justify-center">
              <p className="text-[#7C879D] text-[0.8vw] font-bold">
                Total Items
              </p>
              <h2 className="text-[#1F4B7F] text-[1.7vw] leading-[2vw] font-bold">
                {dashboardData?.total_data || 0}
              </h2>
            </div>

            <div className="bg-[#E9EEF5] rounded-[0.65vw] p-[0.5vw] flex flex-col justify-center">
              <p className="text-[#7C879D] text-[0.8vw] font-bold">Pinned</p>
              <h2 className="text-[#1F4B7F] text-[1.7vw] leading-[2vw] font-bold">
                {dashboardData?.pinned || 0}
              </h2>
            </div>

            <div className="bg-[#E9EEF5] rounded-[0.65vw] p-[0.5vw] flex flex-col justify-center">
              <p className="text-[#7C879D] text-[0.8vw] font-bold">Expiring</p>
              <h2 className="text-[#FF9900] text-[1.7vw] leading-[2vw] font-bold">
                {dashboardData?.expiring || 0}
              </h2>
            </div>

            <div className="bg-[#E9EEF5] rounded-[0.65vw] p-[0.5vw] flex flex-col justify-center">
              <p className="text-[#7C879D] text-[0.8vw] font-bold">Expired</p>
              <h2 className="text-red-500 text-[1.7vw] leading-[2vw] font-bold">
                {dashboardData?.expired || 0}
              </h2>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-[1vw] flex-1 min-h-0">
        <div className="w-[65%] bg-white/75 rounded-[0.9vw] border-[0.1vw] border-[#1F4B7F] overflow-hidden shadow-sm">
          <div className="bg-[#1F4B7F] text-white px-[1vw] h-[6vh] flex justify-between items-center">
            <h3 className="text-[1.05vw] font-bold">Recent Activity</h3>

            <button
              onClick={() => setActiveTab("logs")}
              className="text-[0.85vw] font-bold underline"
            >
              Full log
            </button>
          </div>

          <div className="p-[0.7vw] h-[calc(100%-6vh)] overflow-y-auto">
            {recentActivity.length > 0 ? (
              <table className="w-full table-fixed border-collapse">
                <tbody>
                  {recentActivity.map((item, index) => (
                    <tr
                      key={item.id || index}
                      className={`h-[5vh] ${
                        index !== recentActivity.length - 1
                          ? "border-b-[0.1vw] border-[#D7DFEA]"
                          : ""
                      }`}
                    >
                      <td className="w-[18%] text-[#1F4B7F] text-[0.85vw] font-bold truncate pr-[0.8vw]">
                        {item.user_name || "-"}
                      </td>

                      <td className="w-[16%] pr-[0.8vw]">
                        <span className="bg-[#E9EEF5] text-[#1F4B7F] text-[0.6vw] px-[0.35vw] py-[0.05vw] rounded-[0.25vw] font-bold whitespace-nowrap">
                          {formatAction(item.action)?.length > 21 ? (
                            <Tooltip
                              title={formatAction(item.action)}
                              color="white"
                              overlayInnerStyle={{ color: "#1F487C" }}
                            >
                              <span className="cursor-pointer">
                                {formatAction(item.action).slice(0, 15)}...
                              </span>
                            </Tooltip>
                          ) : (
                            formatAction(item.action)
                          )}
                        </span>
                      </td>

                      <td className="w-[18%] text-[0.72vw] text-[#7C879D] truncate pr-[0.8vw]">
                        <span className="font-bold">User:</span>{" "}
                        {item.user_id || "-"}
                      </td>

                      <td className="w-[32%] text-[0.72vw] text-[#7C879D] truncate pr-[0.8vw]">
                        <span className="font-bold">IP:</span>{" "}
                        {/* {item?.ip_address
                          ?.split("::ffff:")?.[1]
                          .replace("/128", "") || "-"} */}
                          {item?.ip_address}
                      </td>

                      <td className="w-[16%] text-[#7C879D] text-[0.72vw] font-semibold text-right whitespace-nowrap">
                        {item.last_seen || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="h-full flex items-center justify-center text-[#7C879D] text-[1vw] font-semibold">
                No Recent Activity
              </div>
            )}
          </div>
        </div>
        <div className="w-[35%] bg-white/75 rounded-[0.9vw] border-[0.1vw] border-[#1F4B7F] overflow-hidden shadow-sm">
          <div className="bg-[#1F4B7F] text-white px-[1vw] h-[6vh] flex justify-between items-center">
            <h3 className="text-[1.05vw] font-bold">Pinned Items</h3>
          </div>

          <div className="h-[calc(100%-6vh)] overflow-y-auto">
            {pinnedCredentials.length > 0 ? (
              pinnedCredentials.map((item, index) => (
                <div
                  key={
                    item.cred_id || item.document_id || item.contact_id || index
                  }
                  className={`flex items-center justify-between px-[1vw] h-[7.1vh] ${
                    index % 2 === 0 ? "bg-[#E9EEF5]" : "bg-white"
                  }`}
                >
                  <div className="flex items-center gap-[0.8vw] min-w-0">
                    <div className="w-[2vw] h-[2vw] rounded-[0.4vw] bg-[#1F4B7F] text-white flex items-center justify-center font-bold text-[0.85vw] shrink-0">
                      {getPinnedType(item)}
                    </div>

                    <div className="min-w-0">
                      {getPinnedTitle(item)?.length > 35 ? (
                        <Tooltip
                          placement="top"
                          title={getPinnedTitle(item)}
                          color="white"
                          overlayInnerStyle={{ color: "#1F487C" }}
                        >
                          <h4 className="text-[#1F4B7F] text-[0.95vw] leading-[1.1vw] font-bold truncate cursor-pointer">
                            {getPinnedTitle(item)}
                          </h4>
                        </Tooltip>
                      ) : (
                        <h4 className="text-[#1F4B7F] text-[0.95vw] leading-[1.1vw] font-bold truncate">
                          {getPinnedTitle(item)}
                        </h4>
                      )}

                      <p className="text-[#7C879D] text-[0.75vw] leading-[1vw] font-medium truncate">
                        {getPinnedSubtitle(item)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-center shrink-0">
                    {item.document_id ? (
                      <Tooltip
                        title="Download"
                        color="white"
                        overlayInnerStyle={{ color: "#1F487C" }}
                      >
                        <MdOutlineFileDownload
                          size="1.25vw"
                          color="#1F487C"
                          className="cursor-pointer"
                          onClick={() => handlePinnedDocumentDownload(item)}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title="View"
                        color="white"
                        overlayInnerStyle={{ color: "#1F487C" }}
                      >
                        <MdVisibility
                          size="1.25vw"
                          color="#1F487C"
                          className="cursor-pointer"
                          onClick={() => handlePinnedView(item)}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="h-full flex items-center justify-center text-[#7C879D] text-[1vw] font-semibold">
                No Pinned Items Found
              </div>
            )}
          </div>
        </div>
      </div>

      <DocumentOtpModal
        show={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          setOtpDocument(null);
        }}
        document={otpDocument}
        actionType="download_document"
        onVerifySuccess={handleHomeDocumentOtpVerify}
        resendOtp={async () => {
          return await RequestOtpForViewSensitiveDocument({
            user_id: getUserId(),
            document_id: otpDocument?.document_id,
          });
        }}
      />

      <SecretAccessModal
        show={secretModalOpen}
        credId={selectedCredId}
        otpPurpose={otpPurpose}
        mode="view"
        verified={verified}
        setVerified={setVerified}
        onClose={() => {
          setSecretModalOpen(false);
          setSelectedCredId(null);
        }}
      />

      <ContactViewModal
        open={contactViewOpen}
        onClose={() => {
          setContactViewOpen(false);
          setSelectedContact(null);
        }}
        contact={selectedContact}
      />
    </div>
  );
};

export default Home;
